/**
 * All toolbar button configurations — SVG icons, commands, dropdown options.
 */
export interface SingleButtonConfig {
  keyname: string;
  label: string;
  cmd?: string;
  value?: string;
  dropdown?: false;
  dropdownOptions?: never;
}

export interface DropdownButtonConfig {
  keyname: string;
  label: string;
  dropdown: true;
  dropdownOptions: Record<string, { label: string; cmd: string; value: string }>;
  cmd?: never;
  value?: never;
}

export type AnyButtonConfig = SingleButtonConfig | DropdownButtonConfig;

export type ButtonConfigMap = typeof BUTTON_CONFIGS;

export const BUTTON_CONFIGS = {
  bold: {
    keyname: 'bold',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M6 12h9a4 4 0 0 1 0 8H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h7a4 4 0 0 1 0 8"/></svg>`,
    cmd: 'bold',
  },
  italic: {
    keyname: 'italic',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><line x1="19" x2="10" y1="4" y2="4"/><line x1="14" x2="5" y1="20" y2="20"/><line x1="15" x2="9" y1="4" y2="20"/></svg>`,
    cmd: 'italic',
  },
  underline: {
    keyname: 'underline',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M6 4v6a6 6 0 0 0 12 0V4"/><line x1="4" x2="20" y1="20" y2="20"/></svg>`,
    cmd: 'underline',
  },
  strikethrough: {
    keyname: 'strikethrough',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M16 4H9a3 3 0 0 0-2.83 4"/><path d="M14 12a4 4 0 0 1 0 8H6"/><line x1="4" x2="20" y1="12" y2="12"/></svg>`,
    cmd: 'strikeThrough',
  },
  undo: {
    keyname: 'undo',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M9 14 4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11"/></svg>`,
    cmd: 'undo',
  },
  redo: {
    keyname: 'redo',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="m15 14 5-5-5-5"/><path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5A5.5 5.5 0 0 0 9.5 20H13"/></svg>`,
    cmd: 'redo',
  },
  superscript: {
    keyname: 'superscript',
    label: 'x²',
    cmd: 'superscript',
  },
  subscript: {
    keyname: 'subscript',
    label: 'x₂',
    cmd: 'subscript',
  },
  orderedList: {
    keyname: 'orderedList',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><line x1="10" x2="21" y1="6" y2="6"/><line x1="10" x2="21" y1="12" y2="12"/><line x1="10" x2="21" y1="18" y2="18"/><path d="M4 6h1v4"/><path d="M4 10h2"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/></svg>`,
    cmd: 'insertOrderedList',
  },
  unorderedList: {
    keyname: 'unorderedList',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 12h.01"/><path d="M3 18h.01"/><path d="M3 6h.01"/><path d="M8 12h13"/><path d="M8 18h13"/><path d="M8 6h13"/></svg>`,
    cmd: 'insertUnorderedList',
  },
  uppercase: {
    keyname: 'uppercase',
    label: 'ABC',
  },
  lowercase: {
    keyname: 'lowercase',
    label: 'abc',
  },
  toggleCase: {
    keyname: 'toggleCase',
    label: 'Tg',
  },
  headings: {
    keyname: 'headings',
    label: 'Heading',
    dropdown: true,
    dropdownOptions: {
      paragraph: { label: 'Paragraph', cmd: 'formatBlock', value: '<p>' },
      heading1: { label: 'Heading 1', cmd: 'formatBlock', value: '<h1>' },
      heading2: { label: 'Heading 2', cmd: 'formatBlock', value: '<h2>' },
      heading3: { label: 'Heading 3', cmd: 'formatBlock', value: '<h3>' },
      heading4: { label: 'Heading 4', cmd: 'formatBlock', value: '<h4>' },
      heading5: { label: 'Heading 5', cmd: 'formatBlock', value: '<h5>' },
      heading6: { label: 'Heading 6', cmd: 'formatBlock', value: '<h6>' },
      blockquote: { label: 'Blockquote', cmd: 'formatBlock', value: 'blockquote' },
    },
  },
  fonts: {
    keyname: 'fonts',
    label: 'Font',
    dropdown: true,
    dropdownOptions: {
      arial: { label: 'Arial', cmd: 'fontName', value: 'Arial' },
      georgia: { label: 'Georgia', cmd: 'fontName', value: 'Georgia' },
      verdana: { label: 'Verdana', cmd: 'fontName', value: 'Verdana' },
      'courier-new': { label: 'Courier New', cmd: 'fontName', value: 'Courier New' },
      'times-new-roman': { label: 'Times New Roman', cmd: 'fontName', value: 'Times New Roman' },
    },
  },
  textAlignment: {
    keyname: 'textAlignment',
    label: 'Align',
    dropdown: true,
    dropdownOptions: {
      left: { label: 'Left', cmd: 'justifyLeft', value: 'left' },
      center: { label: 'Center', cmd: 'justifyCenter', value: 'center' },
      right: { label: 'Right', cmd: 'justifyRight', value: 'right' },
      justify: { label: 'Justify', cmd: 'justifyFull', value: 'justify' },
    },
  },
  codeBlock: {
    keyname: 'codeBlock',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>`,
  },
  codeInline: {
    keyname: 'codeInline',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="m18 8 4 4-4 4"/><path d="m6 8-4 4 4 4"/><path d="M8 12h.01"/><path d="M12 12h.01"/><path d="M16 12h.01"/></svg>`,
  },
  backgroundColor: {
    keyname: 'backgroundColor',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="m14.622 17.897-10.68-2.913"/><path d="M18.376 2.622a1 1 0 1 1 3.002 3.002L17.36 9.643a.5.5 0 0 0 0 .707l.944.944a2.41 2.41 0 0 1 0 3.408l-.944.944a.5.5 0 0 1-.707 0L8.354 7.348a.5.5 0 0 1 0-.707l.944-.944a2.41 2.41 0 0 1 3.408 0l.944.944a.5.5 0 0 0 .707 0z"/><path d="M9 8c-1.804 2.71-3.97 3.46-6.583 3.948a.507.507 0 0 0-.302.819l7.32 8.883a1 1 0 0 0 1.185.204C12.735 20.405 16 16.792 16 15"/></svg>`,
  },
  textColor: {
    keyname: 'textColor',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="m11 10 3 3"/><path d="M6.5 21A3.5 3.5 0 1 0 3 17.5a2.62 2.62 0 0 1-.708 1.792A1 1 0 0 0 3 21z"/><path d="M9.969 17.031 21.378 5.624a1 1 0 0 0-3.002-3.002L6.967 14.031"/></svg>`,
  },
  imageUpload: {
    keyname: 'imageUpload',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>`,
  },
  fileUpload: {
    keyname: 'fileUpload',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/></svg>`,
  },
  link: {
    keyname: 'link',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>`,
  },
  removeFormat: {
    keyname: 'removeFormat',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 7V4h16v3"/><path d="M5 20h6"/><path d="M13 4 8 20"/><path d="m15 15 5 5"/><path d="m20 15-5 5"/></svg>`,
    cmd: 'removeFormat',
  },
  spellCheck: {
    keyname: 'spellCheck',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><text x="2" y="16" font-size="13" stroke="none" fill="currentColor" font-family="sans-serif" font-weight="bold">ABC</text><path d="M3 20 Q6 17 9 20 Q12 23 15 20" stroke-width="2" stroke-linecap="round" fill="none"/></svg>`,
  },
  table: {
    keyname: 'table',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 3v18"/><rect x="3" y="3" rx="2"/><path d="M3 9h18"/><path d="M3 15h18"/></svg>`,
  },
  hr: {
    keyname: 'hr',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><line x1="4" y1="12" x2="20" y2="12"/></svg>`,
  },
  insertDateTime: {
    keyname: 'insertDateTime',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M8 2v4"/><path d="M16 2v4"/><rect x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/><path d="M16 18h.01"/></svg>`,
  },
  indent: {
    keyname: 'indent',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="3 8 7 12 3 16"/><line x1="21" x2="11" y1="12" y2="12"/><line x1="21" x2="11" y1="6" y2="6"/><line x1="21" x2="11" y1="18" y2="18"/></svg>`,
    cmd: 'indent',
  },
  outdent: {
    keyname: 'outdent',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="21 8 17 12 21 16"/><line x1="3" x2="13" y1="12" y2="12"/><line x1="3" x2="13" y1="6" y2="6"/><line x1="3" x2="13" y1="18" y2="18"/></svg>`,
    cmd: 'outdent',
  },
  fullscreen: {
    keyname: 'fullscreen',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`,
  },
  print: {
    keyname: 'print',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect width="12" height="8" x="6" y="14"/></svg>`,
  },
  emoji: {
    keyname: 'emoji',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" x2="9.01" y1="9" y2="9"/><line x1="15" x2="15.01" y1="9" y2="9"/></svg>`,
  },
  wordCount: {
    keyname: 'wordCount',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
  },
  showSource: {
    keyname: 'showSource',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>`,
  },
  overflowMenu: {
    keyname: 'overflowMenu',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>`,
  },
  markdownToggle: {
    keyname: 'markdownToggle',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 3h18v18H3z"/><path d="M7 7v10"/><path d="M7 12l3-3 3 3"/><path d="M13 17V7l4 5 4-5v10" transform="translate(-4,0) scale(0.6,1) translate(7,0)"/><path d="M14 7l2.5 5L19 7v10"/></svg>`,
  },
  importMarkdown: {
    keyname: 'importMarkdown',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>`,
  },
  exportMarkdown: {
    keyname: 'exportMarkdown',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="12" x2="12" y2="18"/><polyline points="9 15 12 18 15 15"/></svg>`,
  },
  highlight: {
    keyname: 'highlight',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="m9 11-6 6v3h9l3-3"/><path d="m22 12-4.6 4.6a2 2 0 0 1-2.8 0l-5.2-5.2a2 2 0 0 1 0-2.8L14 4"/></svg>`,
  },
  fontSize: {
    keyname: 'fontSize',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><text x="1" y="17" font-size="15" stroke="none" fill="currentColor" font-family="serif" font-weight="bold">A</text><text x="13" y="20" font-size="10" stroke="none" fill="currentColor" font-family="serif">A</text></svg>`,
  },
  taskList: {
    keyname: 'taskList',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="m3 17 2 2 4-4"/><path d="m3 7 2 2 4-4"/><path d="M13 6h8"/><path d="M13 12h8"/><path d="M13 18h8"/></svg>`,
  },
  callout: {
    keyname: 'callout',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`,
  },
  specialChars: {
    keyname: 'specialChars',
    label: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 7V4h16v3"/><path d="M5 20h6"/><path d="M8 4v16"/><path d="m15 15 2 5h.5l2-5"/><path d="M17.4 15a4 4 0 0 0 0 5"/></svg>`,
  },
} as const;
